import classNames from 'classnames';
import { kebabCase } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './ExchangeFromFundEditor.scss';

import config from '../../../config/config';
import { ExchangeTransactionActionType as act } from '../../../redux/modules/exchangeTransaction';
import ExchangeAllShares from '../ExchangeAllShares/ExchangeAllShares';
import CostBasisToolTip from '../../Shared/CostBasisToolTip/CostBasisToolTip';
import CostBasisSnapshot from '../../Sell/CostBasisSnapshot/CostBasisSnapshot';
import If from '../../Shared/If/If';
import MultiInput from '../../Shared/MultiInput/MultiInput';
import {
  UNIT,
  isCostBasisApplicable,
  APP_MINIMUM_BALANCE,
  ExchangeType,
} from '../../../settings/settings';
import { openExternalLinkInPane } from '../../../utils/link-helper';
import { isCollegeExchange } from '../../../utils/transactions-util';
import { exchangeFundInputCloseoutWatcher } from '../../../utils/funds/funds-utility';
import { isAPP } from 'src/utils/advice/advice-utility';
import MarketVolatilityNote from '../../Buy/BuyFlow/ExpandedFundList/MarketVolatilityNote';
import ExpandCollapse from 'src/components/Shared/ExpandCollapse/ExpandCollapse';
import NonCoveredShares from 'src/components/Sell/SellFundEditor/NonCoveredShares/NonCoveredShares';
import WhatWillIPayInTaxes from 'src/components/Sell/SellFundEditor/WhatWillIPayInTaxes';

const ExchangeFromFundEditor = ({
  isSalesforceRep,
  customerWebViewEnabled,
  exchangeFromTotalDollars,
  fundType,
  actions: {
    openModal,
    updateExchangeFund,
    updateExchangeFundAmount,
    updateExchangeFundUnit,
  },
  fund,
}) => {
  const {
    agreementID,
    agreementType,
    fundLimitMessage,
    fundName,
    grossAmount,
    grossAmountError,
    grossUnit,
    inFocus,
    ticker,
    closeout,
    transactionLimit,
  } = fund;
  const isAll = grossUnit === UNIT.ALL_SHARES;
  const containerStyle = classNames('exchange-from-fund-editor-container', {
    'exchange-from-fund-editor-suppress-bottom-margin':
      !isCollegeExchange() && isCostBasisApplicable(agreementType),
  });
  const taxableDetails = [
    {
      className: 'collapse1',
      heading: 'How are Noncovered Shares redeemed?',
      show: true,
      content: (
        <NonCoveredShares
          coveredShares={fund.coveredShares}
          uncoveredShares={fund.uncoveredShares}
        />
      ),
    },
    {
      className: 'collapse2',
      heading: 'What will I pay in taxes?',
      show: true && !(!customerWebViewEnabled && isSalesforceRep),
      content: <WhatWillIPayInTaxes />,
    },
  ];
  const getExchangeClass = () => {
    if (!(!isCollegeExchange() && isCostBasisApplicable(agreementType))) {
      return 'exchange-from-fund-editor-volatility-note-standalone';
    } else {
      if (
        !isCollegeExchange() &&
        isCostBasisApplicable(agreementType) &&
        fund.sweep
      ) {
        return 'exchange-from-fund-editor-volatility-note-sweep';
      } else {
        return 'exchange-from-fund-editor-volatility-note';
      }
    }
  };
  return (
    <div className={containerStyle}>
      <div
        className={
          !isCollegeExchange() && isCostBasisApplicable(agreementType)
            ? 'exchange-from-fund-editor-left-panel'
            : 'exchange-from-fund-editor-left-panel-only'
        }
      >
        <div className="exchange-from-fund-editor-input-label">
          Enter an Amount
        </div>
        <MultiInput
          allowedUnits={isAPP(fund.productType) ? [UNIT.DOLLARS] : undefined}
          ariaLabel={`Exchange Amount for ${fundName}`}
          autoFocus={fund.selected}
          applyBranding={false}
          defaultValue={grossAmount}
          disabled={
            closeout ||
            (isAPP(fund.productType) &&
              fund.balanceAsOf > 0 &&
              fund.balanceAsOf <= APP_MINIMUM_BALANCE)
          }
          displayErrorMessage={
            grossAmountError ||
            (exchangeFromTotalDollars > transactionLimit.maximum &&
              fundType === ExchangeType['MANY_TO_ONE'])
          }
          error={
            exchangeFromTotalDollars > transactionLimit.maximum &&
            fundType === ExchangeType['MANY_TO_ONE']
              ? fundLimitMessage
              : grossAmountError
          }
          errorClassName={`e2e-input-error-${kebabCase(ticker)}`}
          inputID={`amt-${agreementID}`}
          onBlur={() => {
            updateExchangeFund(act.UPDATE_EXCHANGE_FROM_FUND, {
              agreementID,
              inFocus: undefined,
            });
          }}
          onInputChange={(amount) => {
            updateExchangeFundAmount(agreementID, amount);
            exchangeFundInputCloseoutWatcher(
              amount,
              fund,
              openModal,
              updateExchangeFund
            );
          }}
          onUnitChange={(unit) => updateExchangeFundUnit(agreementID, unit)}
          displayValidationMessage={!grossAmountError}
          validationMessage={fundLimitMessage}
          selectedInput={isAll ? UNIT.SHARES : grossUnit}
          setFocus={inFocus}
        />
        <ExchangeAllShares
          fund={fund}
          openModal={openModal}
          updateExchangeFund={updateExchangeFund}
          grossAmountError={grossAmountError}
        />
        {fund.sweep && (
          <div className="exchange-from-fund-editor-deduction-list-sweep-note">
            <span>Important Note:</span> Before redeeming from a sweep any
            unsettled orders. Trades settle two business days after placement.
            Unsettled trades are not yet reflected in your current current
            available balance.
          </div>
        )}
        <div className={getExchangeClass()}>
          <MarketVolatilityNote productType={fund.productType} />
        </div>
      </div>
      <If test={!isCollegeExchange() && isCostBasisApplicable(agreementType)}>
        <div
          className={
            'exchange-from-fund-editor-cost-basis-snapshot ' +
            (fund.sweep
              ? 'exchange-from-fund-editor-right-panel-sweep'
              : 'exchange-from-fund-editor-right-panel')
          }
        >
          <CostBasisSnapshot
            isSalesforceRep={isSalesforceRep}
            customerWebViewEnabled={customerWebViewEnabled}
            changeAction={() =>
              openExternalLinkInPane(
                `${config.url}/usis/my-accounts/profile/cost-basis`
              )
            }
            fund={fund}
            labelComponent={<CostBasisToolTip styles={styles} />}
          />
          <ExpandCollapse details={taxableDetails} />
        </div>
      </If>
    </div>
  );
};

ExchangeFromFundEditor.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func),
  fund: PropTypes.object,
};

export default ExchangeFromFundEditor;
